import { useState } from 'react';
import { QueryFactory } from 'services/api';
import {
  OperationType,
  ProjectResourceType,
  RevisionListItemDto,
} from 'services/api/api-client.types';
import { Field } from 'components/uikit/Field';
import { DropDownInput } from 'components/uikit/inputs/dropdown/DropDownInput';
import { Loading } from 'components/uikit/suspense/Loading';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import styles from './ImportIntoProjectModal.module.scss';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { FormError } from 'components/uikit/FormError';
import dropdownStyles from 'components/uikit/DropdownSelector.module.scss';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import { FeatureFlags } from 'application/constants/env-variables.ts';
import { showWarningToast } from 'components/uikit/toast/showToast';

type ImportIntoProjectModalProps = {
  onImport: (latestRevision: RevisionListItemDto) => Promise<void>;
  isOpen: boolean;
  onClose: () => void;
  importingNames: string[];
  currentProjectId?: number;
  title?: string;
};

export const ImportIntoProjectModal: React.FC<ImportIntoProjectModalProps> = (
  props,
) => {
  const i18n = useScopedTranslation('Component.ImportIntoProjectModal');
  const projectPermissionEnabled = FeatureFlags.projectPermissionsEnabled();
  const projectQuery = QueryFactory.ProjectQuery.useSearchQuery(
    {
      requiredResource: projectPermissionEnabled
        ? ProjectResourceType.Document
        : null,
      requiredOperation: projectPermissionEnabled ? OperationType.Create : null,
    },
    { enabled: props.isOpen },
  );
  const [selectedProject, setSelectedProject] = useState(
    projectQuery.data?.data[0],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      isBlocking={true}
      title={props.title ?? i18n.t('title')}
    >
      <Loading loading={isLoading}>
        <Field title={i18n.t('project_name_field')}>
          <DropDownInput
            className={dropdownStyles.selectorBlack}
            classes={dropdownClasses}
            options={
              projectQuery.data?.data.filter(
                (project) => project.id !== props.currentProjectId,
              ) ?? []
            }
            getOptionLabel={(project) => project.name}
            onValueChanged={(project) => {
              setSelectedProject(project!);
              setErrorMessage(null);
            }}
            value={selectedProject}
            enableSearch={true}
            required={true}
          />
        </Field>
        {errorMessage && <FormError>{errorMessage}</FormError>}

        <div className={styles.footer}>
          <Button
            className={styles.button}
            color={ButtonColor.Secondary}
            title={i18n.t('cancel_button')}
            onClick={props.onClose}
          />
          <Button
            testId="add"
            className={styles.button}
            color={ButtonColor.Default}
            type={'submit'}
            title={i18n.t('add_button')}
            onClick={async () => {
              if (!selectedProject) {
                setErrorMessage(i18n.t('error_select_project'));
                return;
              }

              setIsLoading(true);

              const revisions = await QueryFactory.RevisionClient.search(
                selectedProject!.id,
              );
              const latestRevision = revisions.data.find(
                (revision) =>
                  revision.version === selectedProject?.currentRevision,
              )!;

              const existingReleaseElementsNames = latestRevision.folders.map(
                (folder) => folder.name,
              );
              const hasDuplicateElementsNames = props.importingNames.some(
                (name) => existingReleaseElementsNames.includes(name),
              );

              try {
                await props.onImport(latestRevision);
                props.onClose();
                if (hasDuplicateElementsNames)
                  showWarningToast({
                    content: {
                      description: i18n.t('warning_duplicate_root_names'),
                      title: i18n.t('warning_duplicate_root_names_title'),
                    },
                  });
              } catch (error) {
                setErrorMessage(i18n.t('error_failed_create'));
              } finally {
                setIsLoading(false);
              }
            }}
          />
        </div>
      </Loading>
    </CustomModal>
  );
};

const dropdownClasses = {
  option: dropdownStyles.selectorOption,
  paper: dropdownStyles.selectorPaper,
  popper: dropdownStyles.selectorPopper,
};
